import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Briefcase,
  Building2,
  Clock,
  Droplets,
  Hammer,
  Phone,
  ShieldCheck,
  Sofa,
  Star,
  Store,
  Users,
  Warehouse,
} from "lucide-react";
import Banner from "@/components/Banner";
import { Eyebrow, GoldButton } from "@/components/Reveal";
import { PHOTOS, SERVICES, PROCESS, PHONE_DISPLAY, PHONE_LINK } from "@/data/content";

const ICONS = {
  Building2,
  Briefcase,
  Store,
  Hammer,
  Sofa,
  Droplets,
  Warehouse,
};

const TRUST = [
  { icon: ShieldCheck, label: "Service fiable et discret" },
  { icon: Star, label: "Produits et matériel professionnels" },
  { icon: Clock, label: "Ponctualité garantie" },
  { icon: Users, label: "Équipe professionnelle et discrète" },
];

export default function Services() {
  return (
    <>
      <Banner
        image={PHOTOS.servicesBanner}
        alt="Grand hall d'immeuble lumineux entretenu par PUREVA"
        eyebrow="Nos services"
        title={["Des services adaptés", "à vos espaces."]}
        text="Chez PUREVA, nous offrons des solutions d'entretien professionnelles, adaptées à chaque espace et à chaque besoin. Qualité, fiabilité et discrétion sont au cœur de notre service."
        testid="services-banner-title"
      />

      {/* ---------------- SERVICES GRID ---------------- */}
      <section className="bg-[#F4EFE6] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-4">
              <span className="h-px w-10 bg-gold/70" />
              <Eyebrow>Des solutions sur mesure</Eyebrow>
              <span className="h-px w-10 bg-gold/70" />
            </div>
            <h2 className="mt-5 font-serif text-3xl tracking-[0.08em] text-noir sm:text-4xl">
              NOS SERVICES
            </h2>
            <p className="mt-5 text-[15px] text-neutral-600 sm:text-base">
              Des services de nettoyage professionnels pour tous vos espaces.
            </p>
          </motion.div>

          <div className="mt-14 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon] || Building2;
              return (
                <motion.article
                  key={s.slug}
                  data-testid={`service-card-${s.slug}`}
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.08 }}
                  className="group relative rounded-lg border border-neutral-200 bg-white pb-8 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:border-gold/50"
                >
                  <div className="relative overflow-hidden rounded-t-lg">
                    <img
                      src={s.image}
                      alt={`Service ${s.name} — PUREVA`}
                      loading="lazy"
                      className="h-44 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-noir/50 to-transparent" />
                    <div className="absolute -bottom-7 left-1/2 flex h-14 w-14 -translate-x-1/2 items-center justify-center rounded-full border border-gold/50 bg-noir text-gold">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="px-6 pt-11 text-center">
                    <h3 className="font-serif text-lg tracking-[0.04em] text-noir sm:text-xl">
                      {s.name.toUpperCase()}
                    </h3>
                    <p className="mx-auto mt-3 max-w-xs text-[13px] leading-relaxed text-neutral-500 sm:text-sm">
                      {s.desc}
                    </p>
                    <Link
                      to={`/services/${s.slug}`}
                      data-testid={`service-cta-${s.slug}`}
                      className="mt-4 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#9a7b3f] transition-colors hover:text-gold"
                    >
                      En savoir plus
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </motion.article>
              );
            })}

            {/* Disclaimer filling the last grid cell */}
            <motion.div
              data-testid="services-disclaimer"
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: 0.16 }}
              className="relative rounded-lg border border-gold/40 bg-gold/[0.07] p-8"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9a7b3f]">
                Une précision importante
              </p>
              <p className="mt-4 text-[14px] leading-relaxed text-noir/80">
                PUREVA dessert exclusivement les entreprises, les gestionnaires
                d'immeubles et les espaces commerciaux. Nous ne proposons pas de
                nettoyage de maisons ou d'appartements privés.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---------------- TRUST BAND ---------------- */}
      <section className="bg-noir py-14">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 sm:grid-cols-2 lg:grid-cols-4 lg:px-10">
          {TRUST.map(({ icon: Icon, label }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: i * 0.07 }}
              className={`flex items-center justify-center gap-4 text-center ${
                i > 0 ? "lg:border-l lg:border-gold/20" : ""
              }`}
            >
              <Icon className="h-9 w-9 shrink-0 text-gold" strokeWidth={1.4} />
              <span className="max-w-[160px] text-[11px] font-semibold uppercase leading-snug tracking-[0.12em] text-ivory">
                {label}
              </span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------------- PROCESS ---------------- */}
      <section className="bg-[#F4EFE6] py-20 lg:py-28" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-4">
              <span className="h-px w-10 bg-gold/70" />
              <Eyebrow>Notre méthode</Eyebrow>
              <span className="h-px w-10 bg-gold/70" />
            </div>
            <h2 className="mt-5 font-serif text-3xl tracking-[0.06em] text-noir sm:text-4xl">
              De la demande à l'intervention
            </h2>
          </motion.div>

          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <motion.div
                key={p.step}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="relative text-center"
              >
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-gold/60 bg-white font-serif text-xl italic text-gold">
                  {p.step}
                </span>
                <h3 className="mt-5 text-[13px] font-bold uppercase tracking-[0.1em] text-noir">
                  {p.title}
                </h3>
                <p className="mx-auto mt-3 max-w-xs text-[13px] leading-relaxed text-neutral-500 sm:text-sm">
                  {p.desc}
                </p>
                {i < PROCESS.length - 1 && (
                  <span className="absolute right-0 top-8 hidden h-px w-full max-w-[calc(100%-5rem)] translate-x-full bg-gold/30 lg:block" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="bg-noir py-20 lg:py-24">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 sm:px-10 lg:flex-row lg:items-center lg:justify-between lg:px-10">
          <div>
            <h2 className="font-serif text-3xl leading-snug text-gold sm:text-4xl">
              Besoin d'un service personnalisé ?
            </h2>
            <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-ivory/80 sm:text-base">
              Contactez-nous dès aujourd'hui pour discuter de vos besoins. Nous
              sommes là pour vous aider.
            </p>
            <a
              href={PHONE_LINK}
              data-testid="services-phone-link"
              className="mt-5 inline-flex items-center gap-2.5 text-[15px] font-medium tracking-[0.08em] text-ivory transition-colors hover:text-gold"
            >
              <Phone className="h-4 w-4 text-gold" />
              {PHONE_DISPLAY}
            </a>
          </div>
          <GoldButton as={Link} to="/contact" data-testid="services-bottom-cta">
            Demander une soumission
            <ArrowRight className="h-4 w-4" />
          </GoldButton>
        </div>
      </section>
    </>
  );
}
